import { useMemo } from "react";
import { useUserRoles } from "@/hooks/useUserRoles";
import {
  Permission,
  permissionsForRoles,
  routePermission,
  sectionPermission,
} from "@/lib/permissions";

/**
 * Central permission gate for the UI. Always prefer `can("x")` over role checks
 * so a role definition change updates every screen at once.
 */
export function usePermissions() {
  const { roles, isLoading, isPlatformAdmin } = useUserRoles();

  // Safety net: a signed-in account with no role rows is the practice owner
  // (accounts are provisioned with the admin role at signup).
  const effectiveRoles = useMemo(
    () => (roles.length ? roles : isLoading ? [] : (["admin"] as typeof roles)),
    [roles, isLoading],
  );

  const granted = useMemo(() => permissionsForRoles(effectiveRoles), [effectiveRoles]);

  const can = (p: Permission) => granted.has(p);
  const canAny = (list: Permission[]) => list.some(can);
  const canAll = (list: Permission[]) => list.every(can);
  const canRoute = (path: string) => {
    const required = routePermission(path);
    return required ? can(required) : true;
  };
  const canSection = (title: string) => {
    const required = sectionPermission(title);
    return required ? can(required) : true;
  };

  return {
    isLoading,
    roles: effectiveRoles,
    permissions: granted,
    can,
    canAny,
    canAll,
    canRoute,
    canSection,
    isPlatformAdmin,
  };
}
